/**
 * @fileoverview Responsive design utility hook for consistent UI across devices
 * 
 * This hook provides boolean flags that indicate the current viewport size.
 * It handles hydration mismatches between server and client rendering by:
 * 1. Always returning consistent initial values for SSR
 * 2. Using client-side detection after hydration
 * 3. Ensuring no layout shift during hydration
 * 
 * IMPORTANT: This hook is NOT for:
 * - Pagination or database query management
 * - API response handling
 * - Server-side data fetching
 * 
 * It is ONLY for UI layout decisions based on screen size.
 * 
 * @see {@link src/components/ui/tables/TableInDetails.tsx} - Example of responsive tab layout
 * @see {@link src/components/ui/header/Header.tsx} - Example of responsive header adjustments
 */

'use client';

import { useState, useEffect } from 'react';

/**
 * React hook for detecting viewport breakpoints with SSR safety
 * 
 * Returns boolean flags for different screen sizes:
 * - isMobile: True when viewport width <= 767px
 * - isTablet: True when viewport width <= 1140px
 * 
 * This hook ensures consistent rendering during SSR and client hydration
 * by always returning false during server rendering, then updating based
 * on actual viewport size after client-side hydration.
 * 
 * @returns {Object} Object containing boolean flags for breakpoints
 * @returns {boolean} returns.isMobile - True when viewport width <= 767px
 * @returns {boolean} returns.isTablet - True when viewport width <= 1140px
 */
export const useBreakPoints = () => {
  // Initialize with false to ensure consistent server/client rendering
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    // Mark component as mounted
    setMounted(true);
    
    // Only access window in browser environment
    if (typeof window !== 'undefined') {
      const updateBreakpoints = () => {
        setIsMobile(window.innerWidth <= 767);
        setIsTablet(window.innerWidth <= 1140);
      };
      
      // Initialize breakpoints
      updateBreakpoints();
      
      // Set up resize listener
      window.addEventListener('resize', updateBreakpoints);
      
      // Clean up
      return () => window.removeEventListener('resize', updateBreakpoints);
    }
    // Explicitly return undefined for the SSR case
    return undefined;
  }, []);

  // For SSR or during initial client render, always return false
  // Determine the return value based on the mounted state
  let result = { isMobile, isTablet };
  if (!mounted) {
    // This ensures initial render returns consistent false values
    result = { isMobile: false, isTablet: false };
  }

  // Unconditionally return the determined value
  return result;
};
